import type { QueryResultDetails } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  isUnboundedChatHistoryPage,
  queries,
  type ChatHistoryPage,
} from "../../shared/queries";
import type { ChatEntryData } from "../components/Chat/chatTypes";
import { PAGE_SIZE } from "./useChatHistory";
import { reportZeroDefinitionNotFound } from "../lib/reportZeroDefinitionNotFound";

type Source =
  | {
      type: "workflow";
      chatIds: string[];
      branchId?: string;
      commitId?: string;
    }
  | { type: "commit"; commitId: string }
  | { type: "live"; workflowId: string };
type Page = ChatHistoryPage & {
  entries: readonly ChatEntryData[];
  settled: boolean;
  hasMore: boolean;
  pending: boolean;
  token?: unknown;
  resultType: QueryResultDetails["type"];
};
const emptyEntries: readonly ChatEntryData[] = [];
const range = ({ after, from, limit }: ChatHistoryPage) => ({
  after,
  from,
  limit,
});
const firstPage = (limit: number): Page => ({
  limit,
  entries: emptyEntries,
  settled: false,
  hasMore: false,
  pending: false,
  resultType: "unknown",
});
const workflowQuery = (
  source: Extract<Source, { type: "workflow" }>,
  page: ChatHistoryPage
) =>
  queries.loadWorkflowChatEntries({
    chatIds: source.chatIds,
    branchId: source.branchId,
    commitId: source.commitId,
    ...page,
  });

function receivePage(
  page: Page,
  entries: readonly ChatEntryData[],
  resultType: Page["resultType"]
): Page {
  const complete =
    resultType === "complete" && (entries.length > 0 || !!page.after);
  const hasMore =
    !page.settled && complete ? entries.length === page.limit : page.hasMore;
  // Unbounded history queries return chronological order rather than newest first.
  const orderedEntries = isUnboundedChatHistoryPage(page)
    ? entries.toReversed()
    : entries;
  return {
    ...page,
    entries:
      resultType === "unknown" && !entries.length
        ? page.entries
        : orderedEntries,
    resultType,
    pending: page.pending && resultType === "unknown",
    ...(!page.settled && complete
      ? {
          settled: true,
          hasMore,
          // Exhausted pages must include entries whose timestamps move earlier during reconciliation.
          from: hasMore
            ? { id: entries.at(-1)!.id, createdAt: entries.at(-1)!.createdAt }
            : undefined,
          limit: undefined,
        }
      : {}),
  };
}

function subscribePage(
  z: ReturnType<typeof useZero>,
  source: Source,
  page: ChatHistoryPage,
  listener: (
    entries: readonly ChatEntryData[],
    type: Page["resultType"]
  ) => void
) {
  const view =
    source.type === "workflow"
      ? z.materialize(workflowQuery(source, page))
      : source.type === "commit"
        ? z.materialize(
            queries.loadCommitChat({
              commitId: source.commitId,
              entriesLimit: page.limit,
              entriesAfter: page.after,
              entriesFrom: page.from,
            })
          )
        : z.materialize(
            queries.loadLiveChat({
              workflowId: source.workflowId,
              entriesLimit: page.limit,
              entriesAfter: page.after,
              entriesFrom: page.from,
            })
          );
  view.addListener((data, type, error) => {
    if (type === "error") reportZeroDefinitionNotFound(error);
    const entries =
      source.type === "workflow"
        ? (data as readonly ChatEntryData[])
        : source.type === "commit"
          ? ((data as readonly { entries: readonly ChatEntryData[] }[])[0]
              ?.entries ?? emptyEntries)
          : ((
              data as
                | { chat?: { entries: readonly ChatEntryData[] } }
                | undefined
            )?.chat?.entries ?? emptyEntries);
    listener(entries, type);
  });
  return () => view.destroy();
}

export function useCursorChatHistory(
  key: string,
  source: Source,
  enabled = true,
  initialLimit = PAGE_SIZE
) {
  const z = useZero();
  const sourceKey = JSON.stringify(source);
  const [history, setHistory] = useState(() => ({
    key,
    sourceKey,
    pages: [firstPage(initialLimit)],
  }));
  const pages =
    history.key !== key
      ? [firstPage(initialLimit)]
      : !history.pages[0]!.settled && history.pages[0]!.limit! < initialLimit
        ? [
            { ...history.pages[0]!, limit: initialLimit },
            ...history.pages.slice(1),
          ]
        : history.pages;
  const head = pages[0]!;
  const [workflowEntries, workflowResult] = useQuery(
    source.type === "workflow" ? workflowQuery(source, range(head)) : undefined,
    enabled
  );
  const [chats, commitResult] = useQuery(
    source.type === "commit"
      ? queries.loadCommitChat({
          commitId: source.commitId,
          entriesLimit: head.limit,
          entriesAfter: head.after,
          entriesFrom: head.from,
        })
      : undefined,
    enabled
  );
  const [liveChat, liveResult] = useQuery(
    source.type === "live"
      ? queries.loadLiveChat({
          workflowId: source.workflowId,
          entriesLimit: head.limit,
          entriesAfter: head.after,
          entriesFrom: head.from,
        })
      : undefined,
    enabled
  );
  const token =
    source.type === "workflow"
      ? workflowEntries
      : source.type === "commit"
        ? chats
        : liveChat;
  const result =
    source.type === "workflow"
      ? workflowResult
      : source.type === "commit"
        ? commitResult
        : liveResult;
  const headEntries =
    source.type === "workflow"
      ? (workflowEntries ?? emptyEntries)
      : source.type === "commit"
        ? (chats?.[0]?.entries ?? emptyEntries)
        : (liveChat?.chat?.entries ?? emptyEntries);
  const headChanged =
    enabled && (head.token !== token || head.resultType !== result.type);
  if (pages !== history.pages || history.sourceKey !== sourceKey || headChanged)
    setHistory({
      key,
      sourceKey,
      pages: headChanged
        ? [
            { ...receivePage(head, headEntries, result.type), token },
            ...pages.slice(1),
          ]
        : pages,
    });

  const rangesKey = JSON.stringify(history.pages.slice(1).map(range));
  const viewsRef = useRef(new Map<string, () => void>());
  useEffect(() => {
    const views = viewsRef.current;
    const ranges = enabled ? (JSON.parse(rangesKey) as ChatHistoryPage[]) : [];
    const active = new Set<string>();
    ranges.forEach((page, index) => {
      const signature = JSON.stringify(page);
      const viewKey = `${sourceKey}:${index}:${signature}`;
      active.add(viewKey);
      if (views.has(viewKey)) return;
      views.set(
        viewKey,
        subscribePage(
          z,
          JSON.parse(sourceKey) as Source,
          page,
          (entries, type) => {
            setHistory((current) => {
              const previous = current.pages[index + 1];
              if (
                current.key !== key ||
                current.sourceKey !== sourceKey ||
                !previous ||
                JSON.stringify(range(previous)) !== signature
              )
                return current;
              return {
                ...current,
                pages: current.pages.map((p, i) =>
                  i === index + 1 ? receivePage(p, entries, type) : p
                ),
              };
            });
          }
        )
      );
    });
    for (const [viewKey, destroy] of views)
      if (!active.has(viewKey)) {
        destroy();
        views.delete(viewKey);
      }
  }, [z, key, sourceKey, rangesKey, enabled]);
  useEffect(() => {
    const views = viewsRef.current;
    return () => {
      for (const destroy of views.values()) destroy();
      views.clear();
    };
  }, []);

  const last = history.pages.at(-1)!;
  const loadingOlder = history.pages.some((p) => p.pending);
  // The preserved transcript compares this snapshot by identity during render.
  const entries = useMemo(
    () => history.pages.flatMap((p) => p.entries).toReversed(),
    [history.pages]
  );
  return {
    ready: enabled && result.type === "complete",
    complete:
      enabled &&
      result.type === "complete" &&
      history.pages.every((p) => p.resultType === "complete"),
    entries,
    liveChat,
    loadingOlder,
    onLoadOlder:
      enabled &&
      result.type === "complete" &&
      last.resultType === "complete" &&
      last.hasMore &&
      !loadingOlder
        ? () =>
            setHistory((current) =>
              current.key !== key || current.pages.at(-1) !== last
                ? current
                : {
                    ...current,
                    pages: [
                      ...current.pages,
                      {
                        ...firstPage(PAGE_SIZE),
                        after: last.from,
                        pending: true,
                      },
                    ],
                  }
            )
        : undefined,
  };
}
